import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/shared/components/ui/select'
import { cn } from '@/shared/lib/utils'

const PAGE_SIZE_OPTIONS = [10, 20, 30, 50]

// eslint-disable-next-line react-refresh/only-export-components
export function usePagination(defaultPageSize = 20) {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(defaultPageSize)

  function handlePageSizeChange(newSize: number) {
    setPageSize(newSize)
    setPage(1)
  }

  return { page, setPage, pageSize, setPageSize: handlePageSizeChange }
}

/**
 * Build the visible page list with leading/trailing ellipsis, e.g.
 * `[1, '...', 4, 5, 6, 7, 8, '...', 20]`. Pages are 1-indexed.
 */
function getVisiblePages(currentPage: number, totalPages: number): Array<number | 'ellipsis'> {
  const delta = 2
  const range: number[] = []
  const rangeWithDots: Array<number | 'ellipsis'> = []

  for (
    let i = Math.max(2, currentPage - delta);
    i <= Math.min(totalPages - 1, currentPage + delta);
    i++
  ) {
    range.push(i)
  }

  if (currentPage - delta > 2) {
    rangeWithDots.push(1, 'ellipsis')
  } else {
    rangeWithDots.push(1)
  }

  rangeWithDots.push(...range)

  if (currentPage + delta < totalPages - 1) {
    rangeWithDots.push('ellipsis', totalPages)
  } else if (totalPages > 1) {
    rangeWithDots.push(totalPages)
  }

  return rangeWithDots
}

export function PaginationControls({
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
}: {
  page: number
  pageSize: number
  totalCount: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
}) {
  const { t } = useTranslation()
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  if (totalCount === 0) return null

  const startItem = (page - 1) * pageSize + 1
  const endItem = Math.min(page * pageSize, totalCount)
  const visiblePages = getVisiblePages(page, totalPages)

  const edgeButtonClass =
    'cv-data-table-small size-7 cursor-pointer rounded-[4px] border-[0.5px] border-[var(--rule)] bg-transparent p-0 text-[var(--ink-3)] transition-colors duration-150 hover:bg-[var(--bg-subtle)] hover:text-[var(--ink)] disabled:cursor-default disabled:opacity-30 disabled:hover:bg-transparent'

  return (
    <div className="cv-data-table mt-4 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
          <SelectTrigger className="cv-data-table-small cursor-pointer rounded-[4px] border-[0.5px] border-[var(--rule)] bg-transparent px-1.5 py-0.5 text-[var(--ink-3)]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZE_OPTIONS.map((size) => (
              <SelectItem key={size} value={String(size)}>
                {t('pagination.itemsPerPage', { size })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="cv-data-table-small text-[var(--ink-3)]">
          {t('pagination.itemRange', { start: startItem, end: endItem, total: totalCount })}
        </span>
      </div>

      <nav aria-label={t('pagination.navLabel')} className="flex items-center gap-1">
        <Button
          variant="outline"
          size="icon"
          onClick={() => onPageChange(1)}
          disabled={page <= 1}
          title={t('pagination.firstPage')}
          aria-label={t('pagination.firstPage')}
          className={edgeButtonClass}
        >
          <ChevronsLeft className="size-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page <= 1}
          title={t('common.previous')}
          aria-label={t('common.previous')}
          className={edgeButtonClass}
        >
          <ChevronLeft className="size-4" />
        </Button>

        {visiblePages.map((entry, index) =>
          entry === 'ellipsis' ? (
            <span
              key={`ellipsis-${index}`}
              className="cv-data-table-small flex size-7 items-center justify-center text-[var(--ink-3)]"
              aria-hidden="true"
            >
              …
            </span>
          ) : (
            <Button
              key={entry}
              variant="outline"
              size="icon"
              onClick={() => onPageChange(entry)}
              aria-current={entry === page ? 'page' : undefined}
              className={cn(
                'cv-data-table-small size-7 cursor-pointer rounded-[4px] border-[0.5px] p-0 transition-colors duration-150',
                entry === page
                  ? 'border-[var(--accent-foreground)] bg-[var(--accent)] font-medium text-[var(--accent-foreground)]'
                  : 'border-[var(--rule)] bg-transparent text-[var(--ink-3)] hover:bg-[var(--bg-subtle)] hover:text-[var(--ink)]',
              )}
            >
              {entry}
            </Button>
          ),
        )}

        <Button
          variant="outline"
          size="icon"
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          title={t('common.next')}
          aria-label={t('common.next')}
          className={edgeButtonClass}
        >
          <ChevronRight className="size-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={() => onPageChange(totalPages)}
          disabled={page >= totalPages}
          title={t('pagination.lastPage')}
          aria-label={t('pagination.lastPage')}
          className={edgeButtonClass}
        >
          <ChevronsRight className="size-4" />
        </Button>
      </nav>
    </div>
  );
}
